import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Upload,
  FileSpreadsheet,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  Download,
  Eye,
  Users,
  RotateCcw,
  Play,
  RefreshCw,
} from 'lucide-react';
import logger from '@/utils/logger';
import { API_BASE_URL } from '../config/api.config';
import { getToken } from '@/utils/storage';

const normalizeHeader = (header = '') =>
  header
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

const parseCSVLine = (line, separator) => {
  const values = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === separator && !inQuotes) {
      values.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }

  values.push(current.trim());
  return values;
};

const getDuplicateParentEmailWarnings = async (file) => {
  if (!file) return [];

  try {
    const content = await file.text();
    const lines = content
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line !== '');

    if (lines.length < 2) return [];

    const separator = lines[0].includes(';') ? ';' : ',';
    const headers = parseCSVLine(lines[0], separator).map(normalizeHeader);
    const emailIndex = headers.findIndex((header) => header === 'email_parent' || header === 'email parent');

    if (emailIndex < 0) {
      return ['Colonne email parent non trouvée dans le CSV (email_parent).'];
    }

    const emailToLines = new Map();

    lines.slice(1).forEach((line, idx) => {
      const values = parseCSVLine(line, separator);
      const email = (values[emailIndex] || '').trim().toLowerCase();
      const isValidEmail = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/.test(email);
      if (!email || !isValidEmail) return;

      const lineNumber = idx + 2; // +1 header, +1 index -> ligne réelle CSV
      if (!emailToLines.has(email)) emailToLines.set(email, []);
      emailToLines.get(email).push(lineNumber);
    });

    return [...emailToLines.entries()]
      .filter(([, lineNumbers]) => lineNumbers.length > 1)
      .map(
        ([email, lineNumbers]) =>
          `Email parent dupliqué détecté dans le CSV (${email}) aux lignes ${lineNumbers.join(', ')} : le parent existant sera réutilisé et non recréé.`,
      );
  } catch (error) {
    logger.warn('⚠️ Impossible de générer les warnings email frontend:', error);
    return [];
  }
};

const getParentStatus = (row, details) => {
  if (row?.parentAction === 'created') return 'Parent nouveau';
  if (row?.parentAction === 'reused') return 'Parent existant réutilisé';
  if (row?.parentStatus === 'created') return 'Parent nouveau';
  if (row?.parentStatus === 'reused') return 'Parent existant réutilisé';

  const createdParents = details?.created_parents || [];
  const updatedParents = details?.updated_parents || [];
  const parentName = (row?.parentName || '').trim().toLowerCase();

  if (!parentName || parentName === '-') return 'Aucun parent renseigné';

  const isCreated = createdParents.some(
    (parent) => `${parent.first_name || ''} ${parent.last_name || ''}`.trim().toLowerCase() === parentName,
  );
  if (isCreated) return 'Parent nouveau';

  const isReused = updatedParents.some(
    (parent) => `${parent.first_name || ''} ${parent.last_name || ''}`.trim().toLowerCase() === parentName,
  );
  if (isReused) return 'Parent existant réutilisé';

  return 'Statut parent indisponible';
};

const AdvancedCSVImportModal = ({ isOpen, onClose, onImportComplete }) => {
  // États principaux
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedFile, setSelectedFile] = useState(null);
  const [validationResults, setValidationResults] = useState(null);
  const [importResults, setImportResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  // États de l'interface
  const [dragActive, setDragActive] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  // Étapes du processus
  const steps = [
    { id: 1, title: 'Sélection fichier', icon: Upload },
    { id: 2, title: 'Validation', icon: CheckCircle },
    { id: 3, title: 'Prévisualisation', icon: Eye },
    { id: 4, title: 'Import', icon: Play },
  ];

  // Réinitialiser lors de l'ouverture
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
      setSelectedFile(null);
      setValidationResults(null);
      setImportResults(null);
      setLoading(false);
      setProgress(0);
      setShowPreview(false);
      setShowErrors(false);
    }
  }, [isOpen]);

  // Gestion du drag & drop
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  // Sélection de fichier
  const handleFileSelect = (file) => {
    if (file && (file.type === 'text/csv' || file.name.endsWith('.csv'))) {
      setSelectedFile(file);
      logger.debug('📁 Fichier CSV sélectionné:', {
        name: file.name,
        size: file.size,
        type: file.type,
      });
    } else {
      alert('Veuillez sélectionner un fichier CSV valide');
    }
  };

  // Télécharger le template
  const handleDownloadTemplate = async () => {
    try {
      setLoading(true);
      logger.debug('📥 Téléchargement du template CSV...');

      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/admin/simple-csv-import/template`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'template_import_eleves_parents.csv';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        logger.debug('✅ Template téléchargé avec succès');
      } else {
        throw new Error('Erreur lors du téléchargement du template');
      }
    } catch (error) {
      logger.error('❌ Erreur téléchargement template:', error);
      alert('Erreur lors du téléchargement du template: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Validation du fichier (dry-run)
  const handleValidateFile = async () => {
    if (!selectedFile) return;

    try {
      setLoading(true);
      setProgress(0);

      const formData = new FormData();
      formData.append('file', selectedFile);

      // Simulation de progression
      const progressInterval = setInterval(() => {
        setProgress((prev) => Math.min(prev + 10, 90));
      }, 200);

      const token = getToken();
      // Validation en mode dryRun
      const response = await fetch(`${API_BASE_URL}/admin/simple-csv-import/upload?dryRun=true`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const result = await response.json();

      clearInterval(progressInterval);
      setProgress(100);

      if (result.success) {
        const duplicateEmailWarnings = await getDuplicateParentEmailWarnings(selectedFile);
        const backendWarnings = Array.isArray(result.data.warnings) ? result.data.warnings : [];

        // Stocker les résultats de validation pour la prévisualisation
        setValidationResults({
          valid: result.data.successful,
          invalid: result.data.failed,
          total: result.data.total || 0,
          errors: result.data.errors,
          warnings: [...backendWarnings, ...duplicateEmailWarnings],
          details: result.data,
        });
        setCurrentStep(2); // Rester sur l'étape de validation, passage preview manuel
        logger.debug('✅ Validation terminée:', result.data);
      } else {
        throw new Error(result.error || "Erreur d'import");
      }
    } catch (error) {
      logger.error('❌ Erreur validation:', error);
      alert('Erreur lors de la validation: ' + error.message);
    } finally {
      setLoading(false);
      setProgress(0);
    }
  };

  // Import final
  const handleImportFile = async () => {
    if (!selectedFile) return;

    try {
      setLoading(true);
      setProgress(0);
      logger.debug('🚀 Début import CSV...');

      const formData = new FormData();
      formData.append('file', selectedFile);

      // Simulation de progression
      const progressInterval = setInterval(() => {
        setProgress((prev) => Math.min(prev + 5, 95));
      }, 500);

      const token = getToken();
      // Import réel (sans dryRun)
      const response = await fetch(`${API_BASE_URL}/admin/simple-csv-import/upload`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const result = await response.json();

      clearInterval(progressInterval);
      setProgress(100);

      if (result.success) {
        setImportResults(result.data);
        setCurrentStep(4);
        logger.debug('✅ Import terminé:', result.data);

        // Ne pas notifier le parent tout de suite pour éviter le re-render/reset du modal
        // On attend que l'utilisateur clique sur "Terminer"
      } else {
        throw new Error(result.error || "Erreur d'import");
      }
    } catch (error) {
      logger.error('❌ Erreur import:', error);
      alert("Erreur lors de l'import: " + error.message);
    } finally {
      setLoading(false);
      setProgress(0);
    }
  };

  // Recommencer le processus
  const handleRestart = () => {
    setCurrentStep(1);
    setSelectedFile(null);
    setValidationResults(null);
    setImportResults(null);
    setLoading(false);
    setProgress(0);
    setShowPreview(false);
    setShowErrors(false);
  };

  const handleCloseModal = () => {
    // Si l'import est déjà fait, déclencher aussi le refresh parent
    // même en fermeture via le bouton "X".
    if (onImportComplete && importResults) {
      onImportComplete(importResults);
    }
    onClose();
  };

  // Rendu du contenu selon l'étape
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderFileSelection();
      case 2:
        return renderValidationResults();
      case 3:
        return renderPreview();
      case 4:
        return renderImportResults();
      default:
        return renderFileSelection();
    }
  };

  // Étape 1: Sélection de fichier
  const renderFileSelection = () => (
    <div className="space-y-6">
      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="font-medium text-blue-900 flex items-center">
            <FileSpreadsheet className="mr-2" size={20} />
            Fichier CSV à télécharger et à modifier pour l'import
          </h3>
          <button
            onClick={handleDownloadTemplate}
            disabled={loading}
            className="inline-flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
          >
            <Download size={16} />
            <span>Télécharger le template</span>
          </button>
        </div>
      </div>

      {/* Zone de drop */}
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <Upload className="mx-auto mb-4 text-gray-400" size={48} />
        <p className="text-lg font-medium text-gray-700 mb-2">Glissez votre fichier CSV ici</p>
        <p className="text-sm text-gray-500 mb-4">ou cliquez pour sélectionner un fichier</p>
        <input
          type="file"
          accept=".csv"
          onChange={(e) => e.target.files[0] && handleFileSelect(e.target.files[0])}
          className="hidden"
          id="csv-file-input"
        />
        <label
          htmlFor="csv-file-input"
          className="inline-flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 cursor-pointer"
        >
          Sélectionner un fichier
        </label>
        <p className="mt-1 text-xs text-gray-500">Format accepté : .csv</p>
      </div>

      {/* Fichier sélectionné */}
      {selectedFile && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <FileSpreadsheet className="text-green-600" size={24} />
              <div>
                <p className="font-medium text-green-800">{selectedFile.name}</p>
                <p className="text-sm text-green-600">{(selectedFile.size / 1024).toFixed(1)} KB</p>
              </div>
            </div>
            <button
              onClick={handleValidateFile}
              disabled={loading}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {loading ? <RefreshCw className="animate-spin" size={16} /> : <CheckCircle size={16} />}
              <span>{loading ? 'Import en cours...' : 'Importer le fichier'}</span>
            </button>
          </div>
        </div>
      )}

      {/* Progression */}
      {loading && progress > 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Validation en cours...</span>
            <span className="text-sm text-gray-500">{progress}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );

  // Étape 2: Résultats de validation
  const renderValidationResults = () => (
    <div className="space-y-6">
      {/* Résumé de validation */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
          <CheckCircle className="mx-auto mb-2 text-green-600" size={32} />
          <p className="text-2xl font-bold text-green-800">{validationResults?.valid || 0}</p>
          <p className="text-sm text-green-600">Lignes valides</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
          <AlertCircle className="mx-auto mb-2 text-red-600" size={32} />
          <p className="text-2xl font-bold text-red-800">{validationResults?.invalid || 0}</p>
          <p className="text-sm text-red-600">Lignes avec erreurs</p>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
          <FileSpreadsheet className="mx-auto mb-2 text-blue-600" size={32} />
          <p className="text-2xl font-bold text-blue-800">{validationResults?.total || 0}</p>
          <p className="text-sm text-blue-600">Total lignes</p>
        </div>
      </div>

      {/* Prévisualisation des données */}
      {validationResults?.preview && validationResults.preview.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <h3 className="font-medium text-gray-900">Prévisualisation des données</h3>
            <button onClick={() => setShowPreview(!showPreview)} className="text-blue-600 hover:text-blue-700 text-sm">
              {showPreview ? 'Masquer' : 'Afficher'}
            </button>
          </div>
          {showPreview && (
            <div className="p-4">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 px-3">Ligne</th>
                      <th className="text-left py-2 px-3">Élève</th>
                      <th className="text-left py-2 px-3">Parent 1</th>
                      <th className="text-left py-2 px-3">Garde partagée</th>
                      <th className="text-left py-2 px-3">Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {validationResults.preview.map((row, index) => (
                      <tr key={index} className="border-b border-gray-100">
                        <td className="py-2 px-3">{row.line}</td>
                        <td className="py-2 px-3">{row.student}</td>
                        <td className="py-2 px-3">{row.parent1}</td>
                        <td className="py-2 px-3">
                          {row.has_shared_custody ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                              <Users size={12} className="mr-1" />
                              Oui
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800">
                              Non
                            </span>
                          )}
                        </td>
                        <td className="py-2 px-3">
                          {row.status === 'valid' ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                              <CheckCircle size={12} className="mr-1" />
                              Valide
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-red-100 text-red-800">
                              <AlertCircle size={12} className="mr-1" />
                              Erreur
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Erreurs détaillées */}
      {validationResults?.errors && validationResults.errors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg">
          <div className="px-4 py-3 border-b border-red-200 flex items-center justify-between">
            <h3 className="font-medium text-red-900 flex items-center">
              <AlertTriangle className="mr-2" size={20} />
              Erreurs détectées ({validationResults.errors.length})
            </h3>
            <button onClick={() => setShowErrors(!showErrors)} className="text-red-600 hover:text-red-700 text-sm">
              {showErrors ? 'Masquer' : 'Afficher'}
            </button>
          </div>
          {showErrors && (
            <div className="p-4">
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {validationResults.errors.map((error, index) => (
                  <div key={index} className="text-sm text-red-700 bg-red-100 rounded px-3 py-2">
                    {error}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Warnings */}
      {validationResults?.warnings && validationResults.warnings.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h3 className="font-medium text-yellow-900 mb-2 flex items-center">
            <AlertTriangle className="mr-2" size={20} />
            Avertissements
          </h3>
          <div className="space-y-1">
            {validationResults.warnings.map((warning, index) => (
              <p key={index} className="text-sm text-yellow-700">
                {warning}
              </p>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between">
        <button
          onClick={handleRestart}
          className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
        >
          <RotateCcw size={16} />
          <span>Changer de fichier</span>
        </button>

        {validationResults?.valid > 0 && (
          <button
            onClick={() => setCurrentStep(3)}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Eye size={16} />
            <span>Prévisualiser l'import</span>
          </button>
        )}
      </div>
    </div>
  );

  // Étape 3: Prévisualisation
  const renderPreview = () => (
    <div className="space-y-6">
      {(() => {
        const newCount = validationResults?.details?.created_students?.length || 0;
        const existingCount = validationResults?.details?.updated_students?.length || 0;
        const allExist = newCount === 0 && existingCount > 0;
        return (
          <div
            className={`border rounded-lg p-4 ${allExist ? 'bg-yellow-50 border-yellow-200' : 'bg-blue-50 border-blue-200'}`}
          >
            <h3 className={`font-medium mb-1 ${allExist ? 'text-yellow-900' : 'text-blue-900'}`}>
              {allExist ? 'Aucun nouvel élève à créer' : "Prêt pour l'import"}
            </h3>
            <p className={`text-sm ${allExist ? 'text-yellow-700' : 'text-blue-700'}`}>
              {allExist ? (
                `Tous les élèves (${existingCount}) existent déjà — aucun nouvel enregistrement ne sera créé.`
              ) : (
                <>
                  {newCount > 0 && (
                    <>
                      <strong>
                        {newCount} élève{newCount > 1 ? 's' : ''} nouveau{newCount > 1 ? 'x' : ''}
                      </strong>{' '}
                      {newCount > 1 ? 'seront créés' : 'sera créé'}.{' '}
                    </>
                  )}
                  {existingCount > 0 && (
                    <>
                      {existingCount} élève{existingCount > 1 ? 's' : ''} existant{existingCount > 1 ? 's' : ''}{' '}
                      réutilisé{existingCount > 1 ? 's' : ''} sans modification.{' '}
                    </>
                  )}
                  {validationResults?.invalid > 0 &&
                    `${validationResults.invalid} ligne${validationResults.invalid > 1 ? 's' : ''} avec erreurs ignorée${validationResults.invalid > 1 ? 's' : ''}.`}
                </>
              )}
            </p>
          </div>
        );
      })()}

      {/* Tableau de prévisualisation */}
      {validationResults?.details && (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 font-medium text-gray-700">
            Aperçu des données ({validationResults.details.preview?.length || 0} lignes)
          </div>
          <div className="max-h-64 overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Élève
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Classe
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Parent
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {validationResults.details.preview?.map((row, idx) => {
                  const isNewStudent = row.status === 'Nouveau';
                  const parentStatus = getParentStatus(row, validationResults.details);
                  const isNewParent = parentStatus === 'Parent nouveau';
                  return (
                    <tr key={`preview-${idx}`} className={isNewStudent ? 'bg-green-50' : 'bg-yellow-50'}>
                      <td className="px-4 py-2 text-sm text-gray-900">{row.studentName}</td>
                      <td className="px-4 py-2 text-sm text-gray-500">{row.className}</td>
                      <td className="px-4 py-2 text-sm text-gray-500">{row.parentName}</td>
                      <td className="px-4 py-2 text-sm">
                        <div className="flex flex-col gap-1">
                          <span
                            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              isNewStudent ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                            }`}
                          >
                            Élève {isNewStudent ? 'nouveau' : 'existant'}
                          </span>
                          {parentStatus !== 'Aucun parent renseigné' &&
                            parentStatus !== 'Statut parent indisponible' && (
                              <span
                                className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                  isNewParent ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                                }`}
                              >
                                {parentStatus}
                              </span>
                            )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <button
          onClick={() => setCurrentStep(2)}
          className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
        >
          <RotateCcw size={16} />
          <span>Retour validation</span>
        </button>

        <button
          onClick={handleImportFile}
          disabled={loading}
          className="flex items-center space-x-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
        >
          {loading ? <RefreshCw className="animate-spin" size={16} /> : <Play size={16} />}
          <span>{loading ? 'Import en cours...' : "Lancer l'import"}</span>
        </button>
      </div>

      {/* Progression d'import */}
      {loading && progress > 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Import en cours...</span>
            <span className="text-sm text-gray-500">{progress}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-green-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );

  // Étape 4: Résultats d'import
  const renderImportResults = () => (
    <div className="space-y-6">
      {/* Résumé des résultats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
          <CheckCircle className="mx-auto mb-2 text-green-600" size={32} />
          <p className="text-2xl font-bold text-green-800">{importResults?.successful || 0}</p>
          <p className="text-sm text-green-600">Imports réussis</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
          <AlertCircle className="mx-auto mb-2 text-red-600" size={32} />
          <p className="text-2xl font-bold text-red-800">{importResults?.failed || 0}</p>
          <p className="text-sm text-red-600">Imports échoués</p>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
          <Users className="mx-auto mb-2 text-blue-600" size={32} />
          <p className="text-2xl font-bold text-blue-800">{importResults?.created_parents?.length || 0}</p>
          <p className="text-sm text-blue-600">Parents créés</p>
        </div>
      </div>

      {/* Message de succès */}
      {importResults?.successful > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h3 className="font-medium text-green-900 mb-2 flex items-center">
            <CheckCircle className="mr-2" size={20} />
            Import terminé avec succès !
          </h3>
          <p className="text-sm text-green-700">
            {importResults.successful} élève(s) et leurs parents ont été importés avec succès.
          </p>
          <p className="text-sm text-green-700 mt-1">
            Parents nouveaux: {importResults?.created_parents?.length || 0} - Parents existants réutilisés:{' '}
            {importResults?.updated_parents?.length || 0}
          </p>
        </div>
      )}

      {/* Erreurs d'import */}
      {importResults?.errors && importResults.errors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="font-medium text-red-900 mb-2 flex items-center">
            <AlertTriangle className="mr-2" size={20} />
            Erreurs d'import ({importResults.errors.length})
          </h3>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {importResults.errors.map((error, index) => (
              <p key={index} className="text-sm text-red-700">
                {error}
              </p>
            ))}
          </div>
        </div>
      )}

      {/* Actions finales */}
      <div className="flex items-center justify-between">
        <button
          onClick={handleRestart}
          className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
        >
          <RotateCcw size={16} />
          <span>Nouvel import</span>
        </button>

        <button
          onClick={() => {
            handleCloseModal();
          }}
          className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <CheckCircle size={16} />
          <span>Terminer</span>
        </button>
      </div>
    </div>
  );

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900 flex items-center">
                <FileSpreadsheet className="mr-3" size={24} />
                Import CSV Avancé - Élèves et Parents
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Importez des élèves avec leurs parents et toutes les fonctionnalités avancées.
              </p>
            </div>
            <button onClick={handleCloseModal} className="text-gray-400 hover:text-gray-600">
              <X size={24} />
            </button>
          </div>

          {/* Progress Steps */}
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center justify-between">
              {steps.map((step, index) => (
                <div key={step.id} className="flex items-center">
                  <div
                    className={`flex items-center space-x-2 ${
                      currentStep === step.id
                        ? 'text-blue-600'
                        : currentStep > step.id
                          ? 'text-green-600'
                          : 'text-gray-400'
                    }`}
                  >
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                        currentStep === step.id
                          ? 'bg-blue-100 text-blue-600'
                          : currentStep > step.id
                            ? 'bg-green-100 text-green-600'
                            : 'bg-gray-100 text-gray-400'
                      }`}
                    >
                      {currentStep > step.id ? <CheckCircle className="w-4 h-4" /> : <step.icon className="w-4 h-4" />}
                    </div>
                    <span className="text-sm font-medium">{step.title}</span>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`w-8 h-0.5 mx-4 ${currentStep > step.id ? 'bg-green-600' : 'bg-gray-300'}`} />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">{renderStepContent()}</div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default AdvancedCSVImportModal;
